//@ts-nocheck
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { useUpdateCustomField } from '@/hooks/api/useCustomFields';
import { CustomFieldDefinition, ProjectType } from '@/types';
import { CustomFieldTypes } from '@/types/enums';
import {
  ArrowLeftRight,
  Calendar,
  Eye,
  EyeOff,
  Hash,
  Minus,
  Plus,
  ToggleLeft,
  Type,
} from 'lucide-react';
import { CreateAttributeForm } from '../CreateAttributeForm';

// Define the icon mapping at the top of the file
const DATA_TYPE_ICONS = {
  [CustomFieldTypes.TEXT]: Type,
  [CustomFieldTypes.NUMBER]: Hash,
  [CustomFieldTypes.BOOLEAN]: ToggleLeft,
  [CustomFieldTypes.DATE]: Calendar,
  [CustomFieldTypes.SELECT]: ArrowLeftRight,
};

type CustomFieldsTabProps = {
  projectType: ProjectType;
};

export const CustomFieldsTab = ({ projectType }: CustomFieldsTabProps) => {
  // State for toggling the create form is now encapsulated here
  const [toggleForm, setToggleForm] = useState(false);

  // The mutation hook is also moved here, where it's actually used
  const { updateCustomField } = useUpdateCustomField();

  const handleAttributeVisibility = (customField: CustomFieldDefinition) => {
    try {
      updateCustomField({
        ...customField,
        isVisible: !customField.isVisible,
      });
    } catch (err) {
      console.error('Failed to update visibility:', err);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Custom Field Attributes</CardTitle>
          <Button
            type="button"
            size="sm"
            className="gap-2"
            onClick={() => setToggleForm(!toggleForm)}
          >
            {toggleForm ? (
              <Minus className="h-4 w-4" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {toggleForm && <CreateAttributeForm selectedProject={projectType} />}

        {projectType?.customFieldDefinitions?.length > 0 ? (
          projectType.customFieldDefinitions.map((field) => {
            const Icon = DATA_TYPE_ICONS[field.fieldType] || Type;
            return (
              <div
                key={field.id}
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  !field.isVisible ? 'opacity-40 grayscale' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <Icon className="h-6 w-6 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col">
                    <div className="font-medium">{field.fieldLabel}</div>
                    <div className="text-sm text-muted-foreground capitalize">
                      {field.fieldType.toLowerCase()}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {field.isRequired && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                      {field.fieldOptions?.map((option: string) => (
                        <Badge
                          key={option}
                          variant="outline"
                          className="text-xs font-normal"
                        >
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAttributeVisibility(field)}
                  >
                    {field.isVisible ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Type className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No custom attributes have been defined.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
